import type { BodyRegionKey, BodySystemTag } from '../types/bodyMapIssue';

export type BodyMapView = 'front' | 'front_oblique' | 'side' | 'back_oblique' | 'back';
export type BodyMapShape = 'ellipse' | 'rect';

export interface BodyRegionViewConfig {
  view: BodyMapView;
  x: number;
  y: number;
  width: number;
  height: number;
  shape: BodyMapShape;
}

export interface BodyRegionDefinition {
  key: BodyRegionKey;
  label: string;
  side: 'left' | 'right' | 'bilateral' | 'central';
  systemTags: BodySystemTag[];
  zoomGroup: 'core' | 'upper' | 'lower';
  views: BodyRegionViewConfig[];
}

export const BODY_REGION_DEFINITIONS: BodyRegionDefinition[] = [
  {
    key: 'head',
    label: 'Head',
    side: 'central',
    systemTags: ['general', 'neurological'],
    zoomGroup: 'upper',
    views: [
      { view: 'front', x: 108, y: 34, width: 44, height: 44, shape: 'ellipse' },
      { view: 'back', x: 108, y: 34, width: 44, height: 44, shape: 'ellipse' },
      { view: 'side', x: 122, y: 38, width: 34, height: 40, shape: 'ellipse' },
    ],
  },
  {
    key: 'neck',
    label: 'Neck',
    side: 'central',
    systemTags: ['musculoskeletal', 'neurological'],
    zoomGroup: 'upper',
    views: [
      { view: 'front', x: 118, y: 78, width: 24, height: 18, shape: 'rect' },
      { view: 'back', x: 118, y: 78, width: 24, height: 18, shape: 'rect' },
      { view: 'side', x: 130, y: 80, width: 18, height: 18, shape: 'rect' },
    ],
  },
  {
    key: 'chest',
    label: 'Chest',
    side: 'central',
    systemTags: ['respiratory', 'cardiometabolic'],
    zoomGroup: 'core',
    views: [
      { view: 'front', x: 92, y: 100, width: 76, height: 58, shape: 'rect' },
      { view: 'back', x: 92, y: 100, width: 76, height: 58, shape: 'rect' },
      { view: 'side', x: 128, y: 104, width: 42, height: 56, shape: 'rect' },
    ],
  },
  {
    key: 'abdomen',
    label: 'Abdomen',
    side: 'central',
    systemTags: ['gastrointestinal'],
    zoomGroup: 'core',
    views: [
      { view: 'front', x: 98, y: 162, width: 64, height: 60, shape: 'rect' },
      { view: 'back', x: 98, y: 162, width: 64, height: 60, shape: 'rect' },
      { view: 'side', x: 132, y: 164, width: 40, height: 62, shape: 'rect' },
    ],
  },
  {
    key: 'pelvis',
    label: 'Pelvis',
    side: 'central',
    systemTags: ['reproductive', 'musculoskeletal'],
    zoomGroup: 'core',
    views: [
      { view: 'front', x: 96, y: 226, width: 68, height: 38, shape: 'rect' },
      { view: 'back', x: 96, y: 226, width: 68, height: 38, shape: 'rect' },
      { view: 'side', x: 130, y: 228, width: 42, height: 40, shape: 'rect' },
    ],
  },
  {
    key: 'upper_back',
    label: 'Upper back',
    side: 'central',
    systemTags: ['musculoskeletal'],
    zoomGroup: 'upper',
    views: [
      { view: 'back', x: 92, y: 102, width: 76, height: 56, shape: 'rect' },
      { view: 'side', x: 126, y: 102, width: 40, height: 54, shape: 'rect' },
    ],
  },
  {
    key: 'lower_back',
    label: 'Lower back',
    side: 'central',
    systemTags: ['musculoskeletal'],
    zoomGroup: 'core',
    views: [
      { view: 'back', x: 98, y: 164, width: 64, height: 56, shape: 'rect' },
      { view: 'side', x: 128, y: 166, width: 40, height: 56, shape: 'rect' },
    ],
  },
  {
    key: 'left_shoulder',
    label: 'Left shoulder',
    side: 'left',
    systemTags: ['musculoskeletal'],
    zoomGroup: 'upper',
    views: [
      { view: 'front', x: 58, y: 96, width: 34, height: 30, shape: 'ellipse' },
      { view: 'back', x: 58, y: 96, width: 34, height: 30, shape: 'ellipse' },
      { view: 'side', x: 112, y: 98, width: 22, height: 28, shape: 'ellipse' },
    ],
  },
  {
    key: 'left_clavicle',
    label: 'Left clavicle',
    side: 'left',
    systemTags: ['musculoskeletal'],
    zoomGroup: 'upper',
    views: [
      { view: 'front', x: 88, y: 96, width: 24, height: 10, shape: 'ellipse' },
      { view: 'front_oblique', x: 92, y: 96, width: 22, height: 10, shape: 'ellipse' },
      { view: 'back_oblique', x: 94, y: 98, width: 20, height: 9, shape: 'ellipse' },
    ],
  },
  {
    key: 'right_clavicle',
    label: 'Right clavicle',
    side: 'right',
    systemTags: ['musculoskeletal'],
    zoomGroup: 'upper',
    views: [
      { view: 'front', x: 148, y: 96, width: 24, height: 10, shape: 'ellipse' },
      { view: 'front_oblique', x: 144, y: 96, width: 22, height: 10, shape: 'ellipse' },
      { view: 'back_oblique', x: 144, y: 98, width: 20, height: 9, shape: 'ellipse' },
    ],
  },
  {
    key: 'right_shoulder',
    label: 'Right shoulder',
    side: 'right',
    systemTags: ['musculoskeletal'],
    zoomGroup: 'upper',
    views: [
      { view: 'front', x: 168, y: 96, width: 34, height: 30, shape: 'ellipse' },
      { view: 'back', x: 168, y: 96, width: 34, height: 30, shape: 'ellipse' },
      { view: 'side', x: 152, y: 98, width: 22, height: 28, shape: 'ellipse' },
    ],
  },
  {
    key: 'left_shoulder_blade',
    label: 'Left shoulder blade',
    side: 'left',
    systemTags: ['musculoskeletal'],
    zoomGroup: 'upper',
    views: [
      { view: 'back', x: 88, y: 108, width: 28, height: 32, shape: 'ellipse' },
      { view: 'back_oblique', x: 96, y: 108, width: 24, height: 30, shape: 'ellipse' },
    ],
  },
  {
    key: 'right_shoulder_blade',
    label: 'Right shoulder blade',
    side: 'right',
    systemTags: ['musculoskeletal'],
    zoomGroup: 'upper',
    views: [
      { view: 'back', x: 144, y: 108, width: 28, height: 32, shape: 'ellipse' },
      { view: 'back_oblique', x: 140, y: 108, width: 24, height: 30, shape: 'ellipse' },
    ],
  },
  {
    key: 'left_chest_wall',
    label: 'Left chest wall',
    side: 'left',
    systemTags: ['respiratory', 'cardiometabolic', 'musculoskeletal'],
    zoomGroup: 'core',
    views: [
      { view: 'front', x: 92, y: 110, width: 34, height: 48, shape: 'ellipse' },
      { view: 'front_oblique', x: 96, y: 110, width: 32, height: 46, shape: 'ellipse' },
      { view: 'side', x: 126, y: 112, width: 20, height: 44, shape: 'ellipse' },
    ],
  },
  {
    key: 'right_chest_wall',
    label: 'Right chest wall',
    side: 'right',
    systemTags: ['respiratory', 'cardiometabolic', 'musculoskeletal'],
    zoomGroup: 'core',
    views: [
      { view: 'front', x: 134, y: 110, width: 34, height: 48, shape: 'ellipse' },
      { view: 'front_oblique', x: 132, y: 110, width: 32, height: 46, shape: 'ellipse' },
      { view: 'side', x: 144, y: 112, width: 20, height: 44, shape: 'ellipse' },
    ],
  },
  {
    key: 'sternum',
    label: 'Sternum',
    side: 'central',
    systemTags: ['cardiometabolic', 'respiratory', 'musculoskeletal'],
    zoomGroup: 'core',
    views: [
      { view: 'front', x: 123, y: 108, width: 14, height: 54, shape: 'rect' },
      { view: 'front_oblique', x: 124, y: 108, width: 12, height: 54, shape: 'rect' },
      { view: 'side', x: 134, y: 108, width: 8, height: 54, shape: 'rect' },
    ],
  },
  {
    key: 'left_upper_arm',
    label: 'Left upper arm',
    side: 'left',
    systemTags: ['musculoskeletal'],
    zoomGroup: 'upper',
    views: [
      { view: 'front', x: 54, y: 126, width: 26, height: 72, shape: 'rect' },
      { view: 'back', x: 54, y: 126, width: 26, height: 72, shape: 'rect' },
      { view: 'side', x: 110, y: 126, width: 22, height: 70, shape: 'rect' },
    ],
  },
  {
    key: 'left_biceps',
    label: 'Left biceps',
    side: 'left',
    systemTags: ['musculoskeletal'],
    zoomGroup: 'upper',
    views: [
      { view: 'front', x: 56, y: 128, width: 22, height: 34, shape: 'ellipse' },
      { view: 'front_oblique', x: 64, y: 128, width: 18, height: 34, shape: 'ellipse' },
    ],
  },
  {
    key: 'left_triceps',
    label: 'Left triceps',
    side: 'left',
    systemTags: ['musculoskeletal'],
    zoomGroup: 'upper',
    views: [
      { view: 'back', x: 56, y: 128, width: 22, height: 34, shape: 'ellipse' },
      { view: 'back_oblique', x: 62, y: 128, width: 18, height: 34, shape: 'ellipse' },
    ],
  },
  {
    key: 'right_upper_arm',
    label: 'Right upper arm',
    side: 'right',
    systemTags: ['musculoskeletal'],
    zoomGroup: 'upper',
    views: [
      { view: 'front', x: 180, y: 126, width: 26, height: 72, shape: 'rect' },
      { view: 'back', x: 180, y: 126, width: 26, height: 72, shape: 'rect' },
      { view: 'side', x: 154, y: 126, width: 22, height: 70, shape: 'rect' },
    ],
  },
  {
    key: 'right_biceps',
    label: 'Right biceps',
    side: 'right',
    systemTags: ['musculoskeletal'],
    zoomGroup: 'upper',
    views: [
      { view: 'front', x: 182, y: 128, width: 22, height: 34, shape: 'ellipse' },
      { view: 'front_oblique', x: 178, y: 128, width: 18, height: 34, shape: 'ellipse' },
    ],
  },
  {
    key: 'right_triceps',
    label: 'Right triceps',
    side: 'right',
    systemTags: ['musculoskeletal'],
    zoomGroup: 'upper',
    views: [
      { view: 'back', x: 182, y: 128, width: 22, height: 34, shape: 'ellipse' },
      { view: 'back_oblique', x: 176, y: 128, width: 18, height: 34, shape: 'ellipse' },
    ],
  },
  {
    key: 'left_elbow',
    label: 'Left elbow',
    side: 'left',
    systemTags: ['musculoskeletal'],
    zoomGroup: 'upper',
    views: [
      { view: 'front', x: 52, y: 198, width: 28, height: 20, shape: 'ellipse' },
      { view: 'back', x: 52, y: 198, width: 28, height: 20, shape: 'ellipse' },
      { view: 'side', x: 108, y: 198, width: 22, height: 18, shape: 'ellipse' },
    ],
  },
  {
    key: 'left_forearm',
    label: 'Left forearm',
    side: 'left',
    systemTags: ['musculoskeletal'],
    zoomGroup: 'upper',
    views: [
      { view: 'front', x: 50, y: 218, width: 24, height: 40, shape: 'rect' },
      { view: 'back', x: 50, y: 218, width: 24, height: 40, shape: 'rect' },
      { view: 'side', x: 108, y: 216, width: 18, height: 40, shape: 'rect' },
    ],
  },
  {
    key: 'right_elbow',
    label: 'Right elbow',
    side: 'right',
    systemTags: ['musculoskeletal'],
    zoomGroup: 'upper',
    views: [
      { view: 'front', x: 180, y: 198, width: 28, height: 20, shape: 'ellipse' },
      { view: 'back', x: 180, y: 198, width: 28, height: 20, shape: 'ellipse' },
      { view: 'side', x: 156, y: 198, width: 22, height: 18, shape: 'ellipse' },
    ],
  },
  {
    key: 'right_forearm',
    label: 'Right forearm',
    side: 'right',
    systemTags: ['musculoskeletal'],
    zoomGroup: 'upper',
    views: [
      { view: 'front', x: 186, y: 218, width: 24, height: 40, shape: 'rect' },
      { view: 'back', x: 186, y: 218, width: 24, height: 40, shape: 'rect' },
      { view: 'side', x: 160, y: 216, width: 18, height: 40, shape: 'rect' },
    ],
  },
  {
    key: 'left_wrist',
    label: 'Left wrist',
    side: 'left',
    systemTags: ['musculoskeletal'],
    zoomGroup: 'upper',
    views: [
      { view: 'front', x: 50, y: 258, width: 24, height: 16, shape: 'ellipse' },
      { view: 'back', x: 50, y: 258, width: 24, height: 16, shape: 'ellipse' },
      { view: 'side', x: 108, y: 258, width: 18, height: 14, shape: 'ellipse' },
    ],
  },
  {
    key: 'right_wrist',
    label: 'Right wrist',
    side: 'right',
    systemTags: ['musculoskeletal'],
    zoomGroup: 'upper',
    views: [
      { view: 'front', x: 186, y: 258, width: 24, height: 16, shape: 'ellipse' },
      { view: 'back', x: 186, y: 258, width: 24, height: 16, shape: 'ellipse' },
      { view: 'side', x: 160, y: 258, width: 18, height: 14, shape: 'ellipse' },
    ],
  },
  {
    key: 'left_hand',
    label: 'Left hand',
    side: 'left',
    systemTags: ['musculoskeletal', 'neurological'],
    zoomGroup: 'upper',
    views: [
      { view: 'front', x: 42, y: 272, width: 34, height: 28, shape: 'ellipse' },
      { view: 'back', x: 42, y: 272, width: 34, height: 28, shape: 'ellipse' },
      { view: 'side', x: 100, y: 272, width: 24, height: 26, shape: 'ellipse' },
    ],
  },
  {
    key: 'left_palm',
    label: 'Left palm',
    side: 'left',
    systemTags: ['musculoskeletal', 'neurological'],
    zoomGroup: 'upper',
    views: [
      { view: 'front', x: 44, y: 274, width: 18, height: 22, shape: 'ellipse' },
      { view: 'front_oblique', x: 52, y: 274, width: 18, height: 22, shape: 'ellipse' },
    ],
  },
  {
    key: 'left_fingers',
    label: 'Left fingers',
    side: 'left',
    systemTags: ['musculoskeletal', 'neurological'],
    zoomGroup: 'upper',
    views: [
      { view: 'front', x: 60, y: 272, width: 14, height: 24, shape: 'ellipse' },
      { view: 'front_oblique', x: 66, y: 272, width: 12, height: 24, shape: 'ellipse' },
    ],
  },
  {
    key: 'right_hand',
    label: 'Right hand',
    side: 'right',
    systemTags: ['musculoskeletal', 'neurological'],
    zoomGroup: 'upper',
    views: [
      { view: 'front', x: 184, y: 272, width: 34, height: 28, shape: 'ellipse' },
      { view: 'back', x: 184, y: 272, width: 34, height: 28, shape: 'ellipse' },
      { view: 'side', x: 162, y: 272, width: 24, height: 26, shape: 'ellipse' },
    ],
  },
  {
    key: 'right_palm',
    label: 'Right palm',
    side: 'right',
    systemTags: ['musculoskeletal', 'neurological'],
    zoomGroup: 'upper',
    views: [
      { view: 'front', x: 188, y: 274, width: 18, height: 22, shape: 'ellipse' },
      { view: 'front_oblique', x: 180, y: 274, width: 18, height: 22, shape: 'ellipse' },
    ],
  },
  {
    key: 'right_fingers',
    label: 'Right fingers',
    side: 'right',
    systemTags: ['musculoskeletal', 'neurological'],
    zoomGroup: 'upper',
    views: [
      { view: 'front', x: 176, y: 272, width: 14, height: 24, shape: 'ellipse' },
      { view: 'front_oblique', x: 172, y: 272, width: 12, height: 24, shape: 'ellipse' },
    ],
  },
  {
    key: 'left_hip',
    label: 'Left hip',
    side: 'left',
    systemTags: ['musculoskeletal'],
    zoomGroup: 'lower',
    views: [
      { view: 'front', x: 86, y: 250, width: 26, height: 26, shape: 'ellipse' },
      { view: 'back', x: 86, y: 250, width: 26, height: 26, shape: 'ellipse' },
      { view: 'side', x: 126, y: 248, width: 18, height: 24, shape: 'ellipse' },
    ],
  },
  {
    key: 'right_hip',
    label: 'Right hip',
    side: 'right',
    systemTags: ['musculoskeletal'],
    zoomGroup: 'lower',
    views: [
      { view: 'front', x: 148, y: 250, width: 26, height: 26, shape: 'ellipse' },
      { view: 'back', x: 148, y: 250, width: 26, height: 26, shape: 'ellipse' },
      { view: 'side', x: 144, y: 248, width: 18, height: 24, shape: 'ellipse' },
    ],
  },
  {
    key: 'left_thigh',
    label: 'Left thigh',
    side: 'left',
    systemTags: ['musculoskeletal'],
    zoomGroup: 'lower',
    views: [
      { view: 'front', x: 92, y: 276, width: 28, height: 92, shape: 'rect' },
      { view: 'back', x: 92, y: 276, width: 28, height: 92, shape: 'rect' },
      { view: 'side', x: 126, y: 274, width: 22, height: 92, shape: 'rect' },
    ],
  },
  {
    key: 'right_thigh',
    label: 'Right thigh',
    side: 'right',
    systemTags: ['musculoskeletal'],
    zoomGroup: 'lower',
    views: [
      { view: 'front', x: 140, y: 276, width: 28, height: 92, shape: 'rect' },
      { view: 'back', x: 140, y: 276, width: 28, height: 92, shape: 'rect' },
      { view: 'side', x: 150, y: 274, width: 22, height: 92, shape: 'rect' },
    ],
  },
  {
    key: 'left_knee',
    label: 'Left knee',
    side: 'left',
    systemTags: ['musculoskeletal'],
    zoomGroup: 'lower',
    views: [
      { view: 'front', x: 92, y: 368, width: 30, height: 24, shape: 'ellipse' },
      { view: 'back', x: 92, y: 368, width: 30, height: 24, shape: 'ellipse' },
      { view: 'side', x: 126, y: 368, width: 24, height: 22, shape: 'ellipse' },
    ],
  },
  {
    key: 'left_shin',
    label: 'Left shin',
    side: 'left',
    systemTags: ['musculoskeletal'],
    zoomGroup: 'lower',
    views: [
      { view: 'front', x: 94, y: 394, width: 12, height: 82, shape: 'rect' },
      { view: 'side', x: 130, y: 390, width: 10, height: 84, shape: 'rect' },
    ],
  },
  {
    key: 'left_calf',
    label: 'Left calf',
    side: 'left',
    systemTags: ['musculoskeletal'],
    zoomGroup: 'lower',
    views: [
      { view: 'back', x: 92, y: 392, width: 22, height: 86, shape: 'rect' },
      { view: 'side', x: 136, y: 392, width: 14, height: 84, shape: 'rect' },
    ],
  },
  {
    key: 'right_knee',
    label: 'Right knee',
    side: 'right',
    systemTags: ['musculoskeletal'],
    zoomGroup: 'lower',
    views: [
      { view: 'front', x: 138, y: 368, width: 30, height: 24, shape: 'ellipse' },
      { view: 'back', x: 138, y: 368, width: 30, height: 24, shape: 'ellipse' },
      { view: 'side', x: 150, y: 368, width: 24, height: 22, shape: 'ellipse' },
    ],
  },
  {
    key: 'right_shin',
    label: 'Right shin',
    side: 'right',
    systemTags: ['musculoskeletal'],
    zoomGroup: 'lower',
    views: [
      { view: 'front', x: 154, y: 394, width: 12, height: 82, shape: 'rect' },
      { view: 'side', x: 160, y: 390, width: 10, height: 84, shape: 'rect' },
    ],
  },
  {
    key: 'right_calf',
    label: 'Right calf',
    side: 'right',
    systemTags: ['musculoskeletal'],
    zoomGroup: 'lower',
    views: [
      { view: 'back', x: 146, y: 392, width: 22, height: 86, shape: 'rect' },
      { view: 'side', x: 152, y: 392, width: 14, height: 84, shape: 'rect' },
    ],
  },
  {
    key: 'left_lower_leg',
    label: 'Left lower leg',
    side: 'left',
    systemTags: ['musculoskeletal'],
    zoomGroup: 'lower',
    views: [
      { view: 'front', x: 96, y: 392, width: 22, height: 86, shape: 'rect' },
      { view: 'back', x: 96, y: 392, width: 22, height: 86, shape: 'rect' },
      { view: 'side', x: 130, y: 390, width: 18, height: 88, shape: 'rect' },
    ],
  },
  {
    key: 'right_lower_leg',
    label: 'Right lower leg',
    side: 'right',
    systemTags: ['musculoskeletal'],
    zoomGroup: 'lower',
    views: [
      { view: 'front', x: 142, y: 392, width: 22, height: 86, shape: 'rect' },
      { view: 'back', x: 142, y: 392, width: 22, height: 86, shape: 'rect' },
      { view: 'side', x: 154, y: 390, width: 18, height: 88, shape: 'rect' },
    ],
  },
  {
    key: 'left_ankle',
    label: 'Left ankle',
    side: 'left',
    systemTags: ['musculoskeletal'],
    zoomGroup: 'lower',
    views: [
      { view: 'front', x: 96, y: 478, width: 22, height: 16, shape: 'ellipse' },
      { view: 'back', x: 96, y: 478, width: 22, height: 16, shape: 'ellipse' },
      { view: 'side', x: 130, y: 478, width: 18, height: 16, shape: 'ellipse' },
    ],
  },
  {
    key: 'right_ankle',
    label: 'Right ankle',
    side: 'right',
    systemTags: ['musculoskeletal'],
    zoomGroup: 'lower',
    views: [
      { view: 'front', x: 142, y: 478, width: 22, height: 16, shape: 'ellipse' },
      { view: 'back', x: 142, y: 478, width: 22, height: 16, shape: 'ellipse' },
      { view: 'side', x: 154, y: 478, width: 18, height: 16, shape: 'ellipse' },
    ],
  },
  {
    key: 'left_foot',
    label: 'Left foot',
    side: 'left',
    systemTags: ['musculoskeletal'],
    zoomGroup: 'lower',
    views: [
      { view: 'front', x: 84, y: 494, width: 42, height: 18, shape: 'ellipse' },
      { view: 'back', x: 84, y: 494, width: 42, height: 18, shape: 'ellipse' },
      { view: 'side', x: 122, y: 492, width: 34, height: 18, shape: 'ellipse' },
    ],
  },
  {
    key: 'left_heel',
    label: 'Left heel',
    side: 'left',
    systemTags: ['musculoskeletal'],
    zoomGroup: 'lower',
    views: [
      { view: 'front', x: 86, y: 496, width: 14, height: 16, shape: 'ellipse' },
      { view: 'back', x: 86, y: 496, width: 14, height: 16, shape: 'ellipse' },
      { view: 'side', x: 122, y: 494, width: 12, height: 16, shape: 'ellipse' },
    ],
  },
  {
    key: 'left_arch',
    label: 'Left arch',
    side: 'left',
    systemTags: ['musculoskeletal'],
    zoomGroup: 'lower',
    views: [
      { view: 'front', x: 96, y: 496, width: 12, height: 14, shape: 'ellipse' },
      { view: 'side', x: 132, y: 494, width: 10, height: 14, shape: 'ellipse' },
    ],
  },
  {
    key: 'left_forefoot',
    label: 'Left forefoot',
    side: 'left',
    systemTags: ['musculoskeletal'],
    zoomGroup: 'lower',
    views: [
      { view: 'front', x: 104, y: 496, width: 16, height: 14, shape: 'ellipse' },
      { view: 'back', x: 104, y: 496, width: 16, height: 14, shape: 'ellipse' },
      { view: 'side', x: 140, y: 494, width: 14, height: 14, shape: 'ellipse' },
    ],
  },
  {
    key: 'left_toes',
    label: 'Left toes',
    side: 'left',
    systemTags: ['musculoskeletal'],
    zoomGroup: 'lower',
    views: [
      { view: 'front', x: 118, y: 497, width: 10, height: 13, shape: 'ellipse' },
      { view: 'back', x: 118, y: 497, width: 10, height: 13, shape: 'ellipse' },
      { view: 'side', x: 152, y: 495, width: 8, height: 12, shape: 'ellipse' },
    ],
  },
  {
    key: 'right_foot',
    label: 'Right foot',
    side: 'right',
    systemTags: ['musculoskeletal'],
    zoomGroup: 'lower',
    views: [
      { view: 'front', x: 134, y: 494, width: 42, height: 18, shape: 'ellipse' },
      { view: 'back', x: 134, y: 494, width: 42, height: 18, shape: 'ellipse' },
      { view: 'side', x: 152, y: 492, width: 34, height: 18, shape: 'ellipse' },
    ],
  },
  {
    key: 'right_heel',
    label: 'Right heel',
    side: 'right',
    systemTags: ['musculoskeletal'],
    zoomGroup: 'lower',
    views: [
      { view: 'front', x: 150, y: 496, width: 14, height: 16, shape: 'ellipse' },
      { view: 'back', x: 150, y: 496, width: 14, height: 16, shape: 'ellipse' },
      { view: 'side', x: 152, y: 494, width: 12, height: 16, shape: 'ellipse' },
    ],
  },
  {
    key: 'right_arch',
    label: 'Right arch',
    side: 'right',
    systemTags: ['musculoskeletal'],
    zoomGroup: 'lower',
    views: [
      { view: 'front', x: 160, y: 496, width: 12, height: 14, shape: 'ellipse' },
      { view: 'side', x: 162, y: 494, width: 10, height: 14, shape: 'ellipse' },
    ],
  },
  {
    key: 'right_forefoot',
    label: 'Right forefoot',
    side: 'right',
    systemTags: ['musculoskeletal'],
    zoomGroup: 'lower',
    views: [
      { view: 'front', x: 168, y: 496, width: 16, height: 14, shape: 'ellipse' },
      { view: 'back', x: 168, y: 496, width: 16, height: 14, shape: 'ellipse' },
      { view: 'side', x: 170, y: 494, width: 14, height: 14, shape: 'ellipse' },
    ],
  },
  {
    key: 'right_toes',
    label: 'Right toes',
    side: 'right',
    systemTags: ['musculoskeletal'],
    zoomGroup: 'lower',
    views: [
      { view: 'front', x: 182, y: 497, width: 10, height: 13, shape: 'ellipse' },
      { view: 'back', x: 182, y: 497, width: 10, height: 13, shape: 'ellipse' },
      { view: 'side', x: 182, y: 495, width: 8, height: 12, shape: 'ellipse' },
    ],
  },
];

export const BODY_REGION_OPTIONS = BODY_REGION_DEFINITIONS.map((region) => ({
  value: region.key,
  label: region.label,
}));

export function getBodyRegionDefinition(regionKey: BodyRegionKey) {
  return BODY_REGION_DEFINITIONS.find((region) => region.key === regionKey) || null;
}
